import { PublicClientApplication, type AuthenticationResult, type Configuration, InteractionRequiredAuthError } from '@azure/msal-browser';

const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_AD_CLIENT_ID,
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: `${window.location.origin}/sso/callback`,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

let msalInstance: PublicClientApplication | null = null;

const getMsalInstance = async (): Promise<PublicClientApplication> => {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
  }
  return msalInstance;
};

const loginScopes = ['openid', 'profile', 'email'];

export const loginWithMicrosoft = async (): Promise<void> => {
  const instance = await getMsalInstance();
  try {
    await instance.loginRedirect({ scopes: loginScopes, prompt: 'select_account' });
  } catch (e: any) {
    if (e?.errorCode === 'interaction_in_progress') {
      await instance.clearCache();
      await instance.loginRedirect({ scopes: loginScopes, prompt: 'select_account' });
    } else {
      throw e;
    }
  }
};

export const handleSsoCallback = async (): Promise<AuthenticationResult | null> => {
  const instance = await getMsalInstance();
  // MSAL internally calls history.replaceState({}) to strip auth params from the URL,
  // which wipes Vue Router's state (back/current/forward/position). Preserve and restore it.
  const savedHistoryState = { ...history.state };
  const result = await instance.handleRedirectPromise();
  if (result !== null && !history.state?.current) {
    history.replaceState(savedHistoryState, '');
  }
  return result;
};

export const acquireTokenSilent = async (): Promise<AuthenticationResult | null> => {
  const instance = await getMsalInstance();
  const accounts = instance.getAllAccounts();
  if (accounts.length === 0)
    return null;

  try {
    return await instance.acquireTokenSilent({ scopes: loginScopes, account: accounts[0] });
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError)
      return null;
    throw e;
  }
};
